import { useEffect, useState } from 'react';
import { randomChoice } from '../../../utils/helpers/helpers';
import { getRandomFromRange } from '../../../utils/data/random/getRandomFromRange';
import { useContent } from '../../../redux/reducer/content';
import { getReward } from '../../../utils/helpers';
import { isNumber } from 'lodash';
import { eventSubscription } from '../../../utils/events/eventSubscription';

export const settings = {
  minEls: 100,
  elsOnBackground: 20,
  background: {
    elScale: [0.2, 1],
    fallDuration: [5, 20],
    fallRotate: [480, 720],
  },
  animation: {
    rollOffsetScale: 0.001,
    fullDuration: 15,
    durationRange: [0.2, 0.4],
    offsetRangeScale: [0.1, 0.4],
    addArrowScale: 0.4,
    acceleration: 0.2,
  },
  particles: {
    arr: new Array(20).fill(0),
    distance: 110,
    speed: 0.01,
  },
  counter: {
    addScaleValue: 0.3,
  },
  shift: {
    min: 10,
    max: 20,
  },
};

export const wheelAnimationLogic = ({
  listRef,
  arrowRef,
  backgroundContainerRef,
  backgroundEls,
  counterParticlesRef,
  particlesContainerRef,
  prizeValueRef,
  prizesRef,
  currentAnimation,
  prizes,
  setPrizeValue,
  setElsOnBackgroundData,
  setIsShowBackBtn,
  isRollEnd,
  setIsRollEnd,
  backgroundData,
  necessaryPrizeIndex,
  t,
}) => {
  const {
    lootBoxes,
    activeLootBoxPrize: { id, type },
  } = useContent();

  const [particlesTweens, setParticlesTweens] = useState([]);

  const onRollEnd = () => {
    const {
      counter: { addScaleValue },
      particles: { distance, speed },
    } = settings;

    const necessaryPrize = lootBoxes[type].prizes.find(({ id: prizeId }) => prizeId === id);

    const { value, type: valueType } = getReward(necessaryPrize, t);

    const timeline = gsap.timeline({
      onComplete: () => {
        timeline.kill();
        setIsShowBackBtn(true);
      },
    });

    timeline
      .to(prizesRef.current[necessaryPrizeIndex], {
        scale: 1.3,
        duration: 0.8,
        ease: 'back.inOut(20)',
        onComplete: () => {
          setElsOnBackgroundData({ prize: prizes[necessaryPrizeIndex], count: settings.elsOnBackground });
        },
      })
      .to(prizeValueRef.current, {
        onStart: () => {
          gsap.set(prizeValueRef.current, { x: '-50%', y: '20%' });
        },
        opacity: 1,
        ease: 'none',
        duration: 0.3,
      });

    if (valueType === 'counter') {
      const startValue = { value: 0 };
      const startScale = gsap.getProperty(prizeValueRef.current, 'scale');

      timeline.to(startValue, {
        onStart() {
          const tween = gsap.to(particlesContainerRef.current, {
            onComplete: () => tween.kill(),
            opacity: 1,
            ease: 'sine.inOut',
            duration: 0.3,
          });

          setParticlesTweens(
            counterParticlesRef.current.map((el) => {
              const angle = Math.random() * Math.PI * 2;
              const [x, y] = ['x', 'y'].map((axis) => Math[axis === 'x' ? 'cos' : 'sin'](angle) * distance);
              const rotate = Math.atan2(
                ...['y', 'x'].map((axis) => (axis === 'x' ? x : y) - gsap.getProperty(el, axis)),
              );
              const tween = gsap.from(el, {
                onStart: () => {
                  gsap.set(el, { opacity: 1, scaleX: 2, rotate: (rotate * 180) / Math.PI });
                },
                x,
                y,
                opacity: 0,
                scaleX: 1,
                ease: 'sine.out',
                duration: (Math.abs(x) + Math.abs(y)) * speed,
                repeat: -1,
              });

              tween.progress(Math.random());

              return tween;
            }),
          );
        },
        onUpdate() {
          setPrizeValue(Math.floor(startValue.value));
          const progress = this.progress();
          gsap.set(prizeValueRef.current, { scale: startScale + progress * addScaleValue, x: '-50%', y: '20%' });
        },
        onComplete() {
          const tween = gsap.to(particlesContainerRef.current, {
            onComplete: () => tween.kill(),
            opacity: 0,
            ease: 'sine.inOut',
            duration: 0.5,
          });
        },
        value: value,
        ease: 'none',
        duration: 1.2,
      });
    }
  };

  useEffect(() => {
    gsap.globalTimeline.resume();
  }, []);

  useEffect(() => {
    if (isRollEnd || !isNumber(id) || !isNumber(necessaryPrizeIndex)) return;

    const onResize = () => {
      const {
        rollOffsetScale,
        fullDuration,
        offsetRangeScale: [minOffsetMultiplier, maxOffsetMultiplier],
        durationRange: [minDuration, maxDuration],
        acceleration,
      } = settings.animation;

      if (currentAnimation.current) {
        currentAnimation.current.progress(0);
        currentAnimation.current.kill();
        currentAnimation.current = null;
      }

      const { left, width } = prizesRef.current[necessaryPrizeIndex].getBoundingClientRect();
      const futurePosition = -(left - (global.innerWidth / 2 - width / 2));
      const necessaryDomEl = prizesRef.current.filter((_, index) => necessaryPrizeIndex !== index);

      currentAnimation.current = gsap.timeline({
        onComplete() {
          currentAnimation.current.kill();
          currentAnimation.current = null;
          setIsRollEnd(true);
        },
      });

      const rollOffset = listRef.current.scrollWidth * rollOffsetScale;
      const offsetX =
        randomChoice(1, -1) * getRandomFromRange(width * minOffsetMultiplier, width * maxOffsetMultiplier);
      const reachingDuration = minDuration + (maxDuration - minDuration) * ((offsetX / width) * maxOffsetMultiplier);
      const distWithOffset = futurePosition + offsetX;
      const [startMove, endMove] = [fullDuration * acceleration, fullDuration * (1 - acceleration)];
      const startDistance = futurePosition * acceleration;

      const updateArrowUp = function () {
        const progress = this.progress();
        const addValue = Math.max(Math.min(progress * 2 - 1, 1), 0) * settings.animation.addArrowScale;
        gsap.set(arrowRef.current, { scaleY: 1 + addValue });
      };

      const updateArrowDown = function () {
        const progress = this.progress();
        const subtractValue = progress * settings.animation.addArrowScale;
        gsap.set(arrowRef.current, { scaleY: 1 + settings.animation.addArrowScale - subtractValue });
      };

      currentAnimation.current
        .to(listRef.current, {
          x: rollOffset,
          delay: 1,
          ease: 'sine.inOut',
          duration: 0.75,
        })
        .to(listRef.current, {
          onUpdate: updateArrowUp,
          x: startDistance,
          duration: startMove,
          ease: 'power3.in',
        })
        .to(listRef.current, {
          onUpdate: updateArrowDown,
          x: distWithOffset,
          duration: endMove,
          ease: 'power3.out',
        })
        .to(listRef.current, {
          x: futurePosition,
          duration: reachingDuration,
          ease: 'sine.onOut',
        })
        .to(necessaryDomEl, {
          opacity: 0,
          ease: 'none',
          duration: 0.3,
        });
    };

    onResize();

    return eventSubscription({ callbacksBus: [{ event: ['resize'], callback: onResize }] });
  }, [prizes, id, necessaryPrizeIndex, isRollEnd]);

  useEffect(() => {
    if (!backgroundEls.current.length) return;

    const fallTimelines = [];

    const showTween = gsap.to(backgroundContainerRef.current, {
      opacity: 1,
      duration: 0.5,
      ease: 'sine.in',
    });

    backgroundEls.current.forEach((el, index, { length }) => {
      const [minDur, maxDur] = settings.background.fallDuration;
      const [minRotate, maxRotate] = settings.background.fallRotate;

      const multiplier = (length - index) / length;
      const fullDuration = minDur + (maxDur - minDur) * multiplier;
      const fullRotate = minRotate + (maxRotate - minRotate) * multiplier;

      const fallTimeline = gsap.timeline({ repeat: -1 });

      fallTimeline
        .to(
          el,
          {
            y: backgroundContainerRef.current.offsetHeight,
            duration: fullDuration,
            ease: 'none',
          },
          0,
        )
        .to(
          el,
          {
            opacity: 1,
            duration: fullDuration * 0.2,
            ease: 'sine.inOut',
          },
          0,
        )
        .to(
          el,
          {
            opacity: 0,
            duration: fullDuration * 0.2,
            ease: 'sine.inOut',
          },
          fullDuration * 0.8,
        )
        .to(
          el,
          {
            rotate: `${fullRotate}deg`,
            duration: fullDuration,
            ease: 'none',
          },
          0,
        );

      fallTimeline.progress(Math.random());

      fallTimelines.push(fallTimeline);
    });

    return () => {
      showTween.kill();
      fallTimelines.forEach((timeline) => timeline.kill());
    };
  }, [backgroundData]);

  useEffect(() => {
    isRollEnd && onRollEnd();
  }, [isRollEnd]);

  useEffect(() => () => particlesTweens.length && particlesTweens.forEach((tween) => tween.kill()), [particlesTweens]);
};
